import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Upload } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { api } from '@/lib/api';

const STATUS_VARIANT = {
  ok: 'success',
  degraded: 'warning',
  down: 'destructive',
  unknown: 'muted',
} as const;

/**
 * Dashboard (UI_DESIGN.md): readiness/quota and current work first, concise
 * metrics with units, and a clear empty state leading to import. No fabricated
 * chart data. Live figures arrive with reporting in M3.
 */
export function DashboardPage() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['readiness'],
    queryFn: api.readiness,
  });

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">Readiness and current work at a glance.</p>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">System readiness</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {isLoading && <p className="text-sm text-muted-foreground">Checking…</p>}
            {isError && <p className="text-sm text-destructive">Could not load readiness.</p>}
            {data && (
              <>
                <div className="flex items-center gap-2">
                  <Badge variant={STATUS_VARIANT[data.status]}>{data.status}</Badge>
                  <span className="text-sm text-muted-foreground">
                    send mode: <span className="font-medium text-foreground">{data.sendMode}</span>
                  </span>
                </div>
                <ul className="space-y-1 text-sm">
                  {data.channels.map((c) => (
                    <li key={c.channel} className="flex items-center justify-between">
                      <span className="capitalize">{c.channel}</span>
                      <Badge variant={c.enabled ? (c.ready ? 'success' : 'warning') : 'muted'}>
                        {c.enabled ? (c.ready ? 'ready' : 'not ready') : 'disabled'}
                      </Badge>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Email quota (this app)</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold tabular-nums">0 / 100</p>
            <p className="text-xs text-muted-foreground">used today · resets at 00:00 UTC</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Active work</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold tabular-nums">0</p>
            <p className="text-xs text-muted-foreground">campaigns sending or scheduled</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recent campaigns</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-start gap-3 rounded-lg border border-dashed p-6">
            <p className="text-sm text-muted-foreground">
              No campaigns yet. Import your contacts to get started.
            </p>
            <Button asChild variant="outline" size="sm">
              <Link to="/contacts">
                <Upload className="size-4" /> Import contacts
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
